import { DesignChannel } from 'components/NewFileModal/Types'
import { StartWithMedia } from '../../components/StartWithMedia/StartWithMedia'
import { PropsWithChildren, useMemo, useState } from 'react'
import { IMediaFile } from 'components/Design/UploadsTab/Types'
import { SetupBasics } from '../../components/SetupBasics/SetupBasics'
import { RefineDetails } from '../../components/RefineDetails/RefineDetails'
import { ChooseTemplate } from '../../components/ChooseTemplate/ChooseTemplate'
import { observer } from 'mobx-react-lite'
import { useMobileWizardCreateDesign } from 'components/Wizards/MobileWizards/hooks/useMobileWizardCreateDesign'
import { MultiStepMobileWizard } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/MultiStepMobileWizard'
import { SubmitStepProps } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/Types'
import { DesignValues, FlowProps } from 'components/Wizards/MobileWizards/Types'
import { useVisualElement } from 'hooks/queries/visualElements/useVisualElement'

type Values = Pick<
  DesignValues,
  | 'initial_media_file_id'
  | 'initial_media_file_remove_background'
  | 'width'
  | 'height'
  | 'filename'
  | 'project'
  | 'source_visual_element_id'
  | 'topic'
  | 'brand_voice_id'
  | 'tone'
  | 'language'
  | 'brand_kit_id'
  | 'content'
>

const FlowGWithInitialTemplate = observer(({ onClose, initialValues, schema, ...props }: FlowProps<DesignChannel>) => {
  const schemaOverride = useMemo(
    () => ({
      ...schema,
      steps: [...schema.steps.slice(0, 3)],
    }),
    [schema],
  )

  const [selectedMedia, setSelectedMedia] = useState<IMediaFile | undefined>()

  return (
    <FlowG initialValues={initialValues} schema={schemaOverride} onClose={onClose} {...props}>
      <StartWithMedia onSelect={setSelectedMedia} hideAltWizard />
      {selectedMedia && <SetupBasics selectedMedia={selectedMedia} hideAspectRatio />}
      <RefineDetails submitLabel="Generate Post" />
    </FlowG>
  )
})

const FlowGWithoutInitialTemplate = observer(
  ({ onClose, schema, initialValues, ...props }: FlowProps<DesignChannel>) => {
    const [selectedMedia, setSelectedMedia] = useState<IMediaFile | undefined>()

    return (
      <FlowG initialValues={initialValues} schema={schema} onClose={onClose} {...props}>
        <StartWithMedia onSelect={setSelectedMedia} />
        {selectedMedia && <SetupBasics selectedMedia={selectedMedia} />}
        <RefineDetails />
        <ChooseTemplate />
      </FlowG>
    )
  },
)

const FlowG = observer(({ children, schema, ...props }: PropsWithChildren<FlowProps<DesignChannel>>) => {
  const {
    meta: { channel },
  } = schema

  const { createDesign } = useMobileWizardCreateDesign(channel, {
    navigateToDesignOnComplete: true,
    loadingToast: 'Generating your post...',
  })

  const handleSubmitStep = async ({ currentStep, totalSteps, values, advance }: SubmitStepProps<Values>) => {
    if (currentStep === totalSteps - 1) {
      await createDesign(values)
      return
    }
    advance()
  }

  return (
    <MultiStepMobileWizard<Values> schema={schema} onSubmitStep={handleSubmitStep} {...props}>
      {children}
    </MultiStepMobileWizard>
  )
})

const FlowGWithTemplateContainer = (
  props: FlowProps<DesignChannel> & { initialValues: Partial<DesignValues> & { source_visual_element_id: number } },
) => {
  const { data: template } = useVisualElement({ id: props.initialValues.source_visual_element_id })

  if (template) {
    return <FlowGWithInitialTemplate {...props} />
  }

  return <FlowGWithoutInitialTemplate {...props} />
}

const FlowGContainer = ({ initialValues, ...props }: FlowProps<DesignChannel>) => {
  const { source_visual_element_id, width, height } = initialValues || {}

  if (source_visual_element_id && width && height) {
    return (
      <FlowGWithTemplateContainer
        initialValues={{ ...initialValues, source_visual_element_id, width, height }}
        {...props}
      />
    )
  }

  return <FlowGWithoutInitialTemplate initialValues={initialValues} {...props} />
}

export { FlowGContainer as FlowG }
